import { Observable } from 'rxjs';
import {
  map,
  groupBy,
  mergeMap,
  scan,
  shareReplay,
  startWith,
  takeUntil,
  filter,
  tap,
} from 'rxjs/operators';
import { idleResource, resource$, ResourceLike } from './resource$';
import { computed, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

export type ResourceByGroup<GroupIdentifier, ResourceState, Params> = {
  [identifier in GroupIdentifier & string]: ResourceLike<ResourceState, Params>;
};

export type ResourceByGroupRef$<GroupIdentifier, ResourceState, Params> = Observable<
  ResourceByGroup<GroupIdentifier, ResourceState, Params>
> & {
  select: (id: GroupIdentifier) => Observable<ResourceLike<ResourceState, Params>>;
  selectSignal: (
    id: Signal<GroupIdentifier | undefined>,
  ) => Signal<ResourceLike<ResourceState, Params>>;
  selectField: <Field extends keyof ResourceLike<ResourceState, Params>>(
    id: GroupIdentifier,
    field: Field,
  ) => Observable<ResourceLike<ResourceState, Params>[Field]>;
  change$: Observable<{
    identifier: GroupIdentifier;
    resource: ResourceLike<ResourceState, Params>;
  }>;
  snapshot: () => ResourceByGroup<GroupIdentifier, ResourceState, Params>;
};

type ResourceMappingByGroup<GroupIdentifier, ResourceState, Params> = {
  resourceByGroup: {
    [identifier in GroupIdentifier & string]: ResourceLike<ResourceState, Params>;
  };
  lastChange$: {
    identifier: GroupIdentifier;
    resource: ResourceLike<ResourceState, Params>;
  };
};
export type ResourceByGroupSetValue$<GroupIdentifier, ResourceState, Params> = (data: {
  resourcesByGroupSnapshot: () =>
    ResourceByGroup<GroupIdentifier, ResourceState, Params> | undefined;
  identifier: (params: NonNullable<Params>) => GroupIdentifier;
}) => Observable<{
  identifier: GroupIdentifier | boolean;
  value: ResourceState | undefined;
}>;

export type ResourceByGroupReload$<GroupIdentifier, ResourceState, Params> = (data: {
  resourcesByGroupSnapshot: () =>
    ResourceByGroup<GroupIdentifier, ResourceState, Params> | undefined;
  identifier: (params: NonNullable<Params>) => GroupIdentifier;
}) => Observable<{
  identifier: GroupIdentifier | 'all';
}>;

export function resourceByGroup$<ResourceState, Params, GroupIdentifier>({
  params$,
  identifier,
  loader,
  initialValue,
  setValue$,
  reload$,
  destroy$,
}: {
  params$: Observable<Params>;
  identifier: (params: NoInfer<NonNullable<Params>>) => GroupIdentifier;
  loader: (param: { params: Params }) => Observable<ResourceState>;
  initialValue?: NoInfer<ResourceState>;
  setValue$?: ResourceByGroupSetValue$<
    NoInfer<GroupIdentifier>,
    NoInfer<ResourceState>,
    NoInfer<Params>
  >;
  reload$?: ResourceByGroupReload$<GroupIdentifier, ResourceState, Params>;
  destroy$: Observable<unknown>;
}): ResourceByGroupRef$<GroupIdentifier, ResourceState, Params> {
  let resourcesByGroupSnapshot:
    undefined | ResourceByGroup<GroupIdentifier, ResourceState, Params> = undefined;
  const resourceByGroup$ = params$.pipe(
    groupBy(
      params => identifier(params as NonNullable<Params>),
      params => params,
    ),
    mergeMap(paramsByGroup$ =>
      resource$({
        params$: paramsByGroup$,
        loader,
        initialValue,
        ...(setValue$
          ? {
              setValue$: () =>
                setValue$({
                  identifier,
                  resourcesByGroupSnapshot: () => resourcesByGroupSnapshot,
                })?.pipe(
                  filter(data => data.identifier === paramsByGroup$.key),
                  map(data => data.value),
                ),
            }
          : {}),
        ...(reload$
          ? {
              reload$: () =>
                reload$({
                  identifier,
                  resourcesByGroupSnapshot: () => resourcesByGroupSnapshot,
                })?.pipe(
                  filter(
                    data => data.identifier === paramsByGroup$.key || data.identifier === 'all',
                  ),
                ),
            }
          : {}),
        destroy$,
      }).pipe(
        map(resource => ({
          resource,
          identifier: paramsByGroup$.key,
        })),
      ),
    ),
    scan(
      (acc, { resource, identifier }) => {
        return {
          resourceByGroup: {
            ...acc.resourceByGroup,
            [identifier as GroupIdentifier & string]: resource,
          },
          lastChange$: {
            identifier,
            resource,
          },
        };
      },
      { resourceByGroup: {}, lastChange$: {} } as ResourceMappingByGroup<
        GroupIdentifier,
        ResourceState,
        Params
      >,
    ),
    startWith({
      resourceByGroup: {},
      lastChange$: {},
    } as ResourceMappingByGroup<GroupIdentifier, ResourceState, Params>),
    tap(({ resourceByGroup }) => (resourcesByGroupSnapshot = resourceByGroup)),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  resourceByGroup$.pipe(takeUntil(destroy$)).subscribe();

  return Object.assign(resourceByGroup$.pipe(map(({ resourceByGroup }) => resourceByGroup)), {
    select: (id: GroupIdentifier) =>
      resourceByGroup$.pipe(
        map(({ resourceByGroup }) => {
          const result =
            resourceByGroup[id as GroupIdentifier & string] || idleResource(initialValue);
          return result;
        }),
      ),
    selectSignal: (id: Signal<GroupIdentifier>) => {
      const resourceByGroupRef = toSignal(resourceByGroup$);
      return computed(
        () =>
          resourceByGroupRef()?.resourceByGroup[id() as GroupIdentifier & string] ??
          idleResource(initialValue),
      );
    },
    selectField: (id: GroupIdentifier, field: keyof ResourceLike<ResourceState, Params>) =>
      resourceByGroup$.pipe(
        map(({ resourceByGroup }) => {
          return (
            resourceByGroup[id as GroupIdentifier & string]?.[field] || idleResource(initialValue)
          );
        }),
      ),
    change$: resourceByGroup$.pipe(
      map(({ lastChange$ }) => lastChange$),
      filter(data => !!data.resource),
    ),
    snapshot: () => resourcesByGroupSnapshot,
  }) as ResourceByGroupRef$<GroupIdentifier, ResourceState, Params>;
}
